import { Controller } from "@hotwired/stimulus"
import jsQR from "jsqr"

const SCAN_INTERVAL_MS = 200
const REPEAT_COOLDOWN_MS = 3000

const RESULT_STYLES = {
  checked_in:         { card: "bg-emerald-600 text-white", frame: "border-emerald-400", icon: "✓" },
  already_checked_in: { card: "bg-amber-500 text-white",   frame: "border-amber-400",   icon: "⚠" },
  wrong_event:        { card: "bg-rose-600 text-white",    frame: "border-rose-500",    icon: "✕" },
  not_found:          { card: "bg-rose-600 text-white",    frame: "border-rose-500",    icon: "✕" }
}

// Entrance ticket scanner: reads QR codes from the camera, posts them to the
// check-in endpoint and shows the result. Keeps the screen awake via the
// Wake Lock API so staff can scan continuously.
export default class extends Controller {
  static targets = ["video", "frame", "scanHint", "cameraError", "result", "resultIcon",
                    "resultMessage", "resultSeat", "resultMeta", "idleHint",
                    "checkedIn", "total", "manualInput"]
  static values = { url: String }

  connect() {
    this.lastCode = null
    this.lastCodeAt = 0
    this.lastScanAt = 0
    this.submitting = false
    this.onVisibilityChange = () => { if (!document.hidden) this.requestWakeLock() }
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    this.detector = "BarcodeDetector" in window ? new BarcodeDetector({ formats: ["qr_code"] }) : null
    this.canvas = document.createElement("canvas")

    this.requestWakeLock()
    this.startCamera()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    if (this.frameRequest) cancelAnimationFrame(this.frameRequest)
    this.stream?.getTracks()?.forEach(track => track.stop())
    this.wakeLock?.release()?.catch(() => {})
    this.audioContext?.close()?.catch(() => {})
  }

  async startCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false
      })
      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()
      this.frameRequest = requestAnimationFrame(timestamp => this.scanLoop(timestamp))
    } catch {
      this.cameraErrorTarget.hidden = false
    }
  }

  async scanLoop(timestamp) {
    this.frameRequest = requestAnimationFrame(t => this.scanLoop(t))
    if (this.submitting || timestamp - this.lastScanAt < SCAN_INTERVAL_MS) return
    if (this.videoTarget.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) return
    this.lastScanAt = timestamp

    const code = this.detector ? await this.detectNative() : this.detectWithJsQR()
    if (code) this.handleCode(code)
  }

  async detectNative() {
    try {
      const barcodes = await this.detector.detect(this.videoTarget)
      return barcodes[0]?.rawValue
    } catch {
      // Some browsers expose BarcodeDetector without QR support; fall back for good.
      this.detector = null
      return null
    }
  }

  detectWithJsQR() {
    const video = this.videoTarget
    this.canvas.width = video.videoWidth
    this.canvas.height = video.videoHeight
    const context = this.canvas.getContext("2d", { willReadFrequently: true })
    context.drawImage(video, 0, 0)
    const image = context.getImageData(0, 0, this.canvas.width, this.canvas.height)
    return jsQR(image.data, image.width, image.height, { inversionAttempts: "dontInvert" })?.data
  }

  handleCode(code) {
    const now = performance.now()
    if (code === this.lastCode && now - this.lastCodeAt < REPEAT_COOLDOWN_MS) {
      this.lastCodeAt = now
      return
    }
    this.lastCode = code
    this.lastCodeAt = now
    this.submit(code)
  }

  submitManual(event) {
    event.preventDefault()
    const code = this.manualInputTarget.value.trim()
    if (code) this.submit(code)
    this.manualInputTarget.value = ""
  }

  async submit(code) {
    if (this.submitting) return
    this.submitting = true

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ code })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.showResult(await response.json())
    } catch {
      this.flashFrame(RESULT_STYLES.not_found.frame)
    } finally {
      this.submitting = false
    }
  }

  showResult(result) {
    const style = RESULT_STYLES[result.status] || RESULT_STYLES.not_found
    const success = result.status === "checked_in"

    this.resultTarget.className = `mt-4 rounded-2xl p-5 text-center shadow-sm ring-1 ring-inset ring-black/5 ${style.card}`
    this.resultIconTarget.textContent = style.icon
    this.resultMessageTarget.textContent = result.message
    this.resultSeatTarget.textContent = result.ticket?.seat_label || ""
    this.resultMetaTarget.textContent = result.ticket
      ? [result.ticket.price_name, result.ticket.sales_channel].filter(Boolean).join(" · ")
      : ""
    this.resultTarget.hidden = false
    if (this.hasIdleHintTarget) this.idleHintTarget.hidden = true

    if (result.stats) {
      this.checkedInTarget.textContent = result.stats.checked_in
      this.totalTarget.textContent = result.stats.total
    }

    this.flashFrame(style.frame)
    this.beep(success)
    navigator.vibrate?.(success ? [80] : [80, 60, 80, 60, 80])
  }

  flashFrame(colorClass) {
    const frame = this.frameTarget
    frame.classList.remove("border-white/70", "border-emerald-400", "border-amber-400", "border-rose-500")
    frame.classList.add(colorClass)
    clearTimeout(this.frameTimeout)
    this.frameTimeout = setTimeout(() => {
      frame.classList.remove(colorClass)
      frame.classList.add("border-white/70")
    }, 1200)
  }

  beep(success) {
    try {
      this.audioContext ||= new (window.AudioContext || window.webkitAudioContext)()
      if (this.audioContext.state === "suspended") this.audioContext.resume()

      const oscillator = this.audioContext.createOscillator()
      const gain = this.audioContext.createGain()
      oscillator.connect(gain)
      gain.connect(this.audioContext.destination)
      oscillator.frequency.value = success ? 1200 : 300
      gain.gain.setValueAtTime(0.25, this.audioContext.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + (success ? 0.15 : 0.4))
      oscillator.start()
      oscillator.stop(this.audioContext.currentTime + (success ? 0.15 : 0.4))
    } catch {
      // Audio feedback is best-effort; vibration and visuals still apply.
    }
  }

  async requestWakeLock() {
    try {
      this.wakeLock = await navigator.wakeLock?.request("screen")
    } catch {
      // Wake lock is unsupported or denied; scanning still works.
    }
  }
}
